import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { RelatedService } from 'src/pages/billing/billing.types';
import { useCreateRelatedServiceMutation } from 'src/pages/billing/billingApi';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { ServiceAgencyFormData, serviceAgencyFormSchema } from '../RelatedService.schema';

type ServiceAgencyFormHookProps = {
  setEditMode: (editMode: boolean) => void;
  relatedService: RelatedService;
};

const defaultValues: ServiceAgencyFormData = {
  serviceType: 'SP',
  startDate: '',
  providerName: '',
  providerPhoneNumber: '',
  agencyName: '',
  agencyPhoneNumber: '',
};

export const useServiceAgencyForm = ({
  setEditMode,
  relatedService,
}: ServiceAgencyFormHookProps) => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [createRelatedService, { isLoading }] = useCreateRelatedServiceMutation();

  const initialValues: ServiceAgencyFormData = {
    serviceType: relatedService.serviceType || defaultValues.serviceType,
    startDate: relatedService.startDate || defaultValues.startDate,
    providerName: relatedService.providerName || defaultValues.providerName,
    providerPhoneNumber: relatedService.providerPhoneNumber || defaultValues.providerPhoneNumber,
    agencyName: relatedService.agencyName || defaultValues.agencyName,
    agencyPhoneNumber: relatedService.agencyPhoneNumber || defaultValues.agencyPhoneNumber,
  };

  const methods = useForm<ServiceAgencyFormData>({
    resolver: zodResolver(serviceAgencyFormSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    try {
      await createRelatedService({
        id: id!,
        payload: {
          serviceType: data.serviceType,
          status: 'ServicedViaAgency',
          startDate: data.startDate,
          providerName: data.providerName,
          providerPhoneNumber: data.providerPhoneNumber,
          agencyName: data.agencyName,
          agencyPhoneNumber: data.agencyPhoneNumber,
        },
      }).unwrap();

      dispatch(addToast({ text: 'Form saved successfully', type: 'positive' }));
      setEditMode(false);
    } catch (error) {
      dispatch(addToast({ text: 'Error saving form', type: 'negative' }));
      console.error('Error saving form:', error);
    }
  });

  return {
    methods,
    handleSubmit,
    isLoading,
  };
};
